import { useState, useEffect, useCallback, useRef } from "react";

import {
  fetchEmployeeProfile,
  fetchTeamMembers,
  fetchReflectionQuestions,
  fetchReminderAPI,
  polishAnswerAPI,
  saveReflectionAPI,
  askAIChatAPI
} from "../../api/dashboard_api";

export default function useDashboard() {
  const email = localStorage.getItem("email");
  const isLoggedIn = localStorage.getItem("isLoggedIn");

  /* UI */
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [activePage, setActivePage] = useState("reflection");

  /* DATA */
  const [profile, setProfile] = useState(null);
  const [team, setTeam] = useState([]);
  const [userRole, setUserRole] = useState(null);

  /* REFLECTION */
  const [period, setPeriod] = useState("daily");
  const [selectedDate, setSelectedDate] = useState("");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [polishing, setPolishing] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  /* SUCCESS POPUP */
  const [successPopup, setSuccessPopup] = useState(null); // { type, title, message }

  /* REMINDERS */
  const [reminderData, setReminderData] = useState(null);
  const [showReminderPopup, setShowReminderPopup] = useState(false);
  const [showReminderPanel, setShowReminderPanel] = useState(false);
  const [hasNewReminder, setHasNewReminder] = useState(false);

  /* AI CHAT */
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);

  // ✅ FINAL FIX: Use sessionStorage so reminder popup shows ONLY ONCE per browser session.
  // useRef resets if component unmounts/remounts. sessionStorage persists across re-renders.
  const reminderShownThisSession = useRef(false);

  useEffect(() => {
    // Mark as shown if sessionStorage already has the flag
    if (sessionStorage.getItem("reminderShown") === "true") {
      reminderShownThisSession.current = true;
    }
  }, []);

  /* RESPONSIVE */
  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth <= 1024);
      if (window.innerWidth <= 1024) setIsSidebarOpen(false);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* LOAD EMPLOYEE */
  const loadEmployee = useCallback(async () => {
    try {
      const { data } = await fetchEmployeeProfile(email);
      setProfile(data.employee);
      setUserRole(data.employee.RoleID);
    } catch (err) {
      console.error("Profile Load Error:", err);
    }
  }, [email]);

  /* LOAD TEAM */
  const loadTeam = useCallback(async () => {
    try {
      const { data } = await fetchTeamMembers(email);
      setTeam(data.records || []);
    } catch (err) {
      console.error("Team Load Error:", err);
    }
  }, [email]);

  /* LOAD QUESTIONS */
  const loadQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await fetchReflectionQuestions(period);

      if (data.status === "success") {
        setQuestions(data.questions);
        setAnswers(data.questions.map(() => [""]));
      } else {
        setQuestions([]);
        setAnswers([]);
      }
    } catch (err) {
      console.error("Get Questions Error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  /* REMINDER FETCH */
  const fetchReminder = useCallback(
    async (p = "daily", options = { showPopup: false }) => {
      try {
        const today = new Date();
        let selected = "";

        if (p === "daily") {
          selected = today.toISOString().split("T")[0];
        } else if (p === "weekly") {
          const year = today.getFullYear();
          const week = String(today.getWeekNumber()).padStart(2, "0");
          selected = `${year}-W${week}`;
        } else if (p === "monthly") {
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, "0");
          selected = `${year}-${month}`;
        }

        const { data } = await fetchReminderAPI(email, p, selected);
        setReminderData(data);

        if (data.hasReminder) {
          setHasNewReminder(true);
          if (options.showPopup) setShowReminderPopup(true);
        } else {
          setHasNewReminder(false);
          if (options.showPopup) setShowReminderPopup(false);
        }
      } catch (err) {
        console.warn("Reminder fetch failed:", err);
        setReminderData(null);
        setHasNewReminder(false);
      }
    },
    [email]
  );

  /* WEEK NUMBER FIX */
  Date.prototype.getWeekNumber = function () {
    const d = new Date(Date.UTC(this.getFullYear(), this.getMonth(), this.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  /* DEFAULT DATE */
  const getDefaultDate = () => {
    const today = new Date();

    if (period === "daily") return today.toISOString().split("T")[0];

    if (period === "weekly") {
      const year = today.getFullYear();
      const week = String(today.getWeekNumber()).padStart(2, "0");
      return `${year}-W${week}`;
    }

    if (period === "monthly") {
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      return `${year}-${month}`;
    }

    return "";
  };

  useEffect(() => {
    setSelectedDate(getDefaultDate());
  }, [period]);

  /* LOCAL UPDATES */
  const updateAnswer = (qi, ai, value) => {
    setAnswers(prev => {
      const copy = prev.map(arr => [...arr]);
      copy[qi][ai] = value;
      return copy;
    });
  };

  const addAnswer = (qi) => {
    setAnswers(prev => {
      const copy = prev.map(arr => [...arr]);
      copy[qi].push("");
      return copy;
    });
  };

  const removeAnswer = (qi, ai) => {
    setAnswers(prev => {
      const copy = prev.map(arr => [...arr]);
      copy[qi].splice(ai, 1);
      if (copy[qi].length === 0) copy[qi].push("");
      return copy;
    });
  };

  /* AI POLISH */
  const polishAnswer = async (qi, ai) => {
    const text = answers[qi][ai];
    if (!text.trim()) return;

    const key = `${qi}-${ai}`;
    setPolishing(p => ({ ...p, [key]: true }));

    try {
      const { data } = await polishAnswerAPI(text.trim());
      const rephrased = data.rephrased || "";

      if (rephrased) {
        setAnswers(prev => {
          const copy = prev.map(arr => [...arr]);
          copy[qi][ai] = rephrased;
          return copy;
        });
      }
    } catch (err) {
      setSuccessPopup({ type: "error", title: "AI Polish Failed", message: "Check backend connection and try again." });
    } finally {
      setPolishing(p => ({ ...p, [key]: false }));
    }
  };

  /* SAVE REFLECTION - UPDATED WITH DUPLICATE HANDLING */
  const saveReflection = async () => {
    setSubmitting(true);

    const cleanJoin = (index) =>
      (answers[index] || [])
        .map(p => p.trim())
        .filter(p => p.length > 0)
        .join("\n");

    const q1 = cleanJoin(0);
    const q2 = cleanJoin(1);
    const q3 = cleanJoin(2);

    if (!q1 || !q2 || !q3) {
      setSuccessPopup({ type: "warning", title: "Incomplete Form", message: "Please fill all 3 questions before submitting." });
      setSubmitting(false);
      return;
    }

    const fd = new FormData();
    fd.append("email", email?.trim() || "");
    fd.append("period", period);
    fd.append("selected_date", selectedDate);
    fd.append("q1", q1);
    fd.append("q2", q2);
    fd.append("q3", q3);

    try {
      const response = await saveReflectionAPI(fd);
      
      // Check if the response indicates a duplicate (successful response with warning message)
      if (response.data && response.data.message && 
          (response.data.message.toLowerCase().includes("already") || 
           response.data.message.toLowerCase().includes("exists"))) {
        setSuccessPopup({ type: "warning", title: "Already Submitted", message: "This reflection has already been submitted for the selected date and period." });
        return;
      }
      
      // Success case - no duplicate
      setSuccessPopup({ type: "success", title: "Reflection Submitted!", message: "Your reflection has been saved successfully ✨" });
      setAnswers(questions.map(() => [""]));
      
    } catch (err) {
      console.error("Save error:", err);
      
      // Check different error patterns for duplicate submissions
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.message || 
                      err.message || "";
      const errorText = errorMsg.toLowerCase();
      
      if (errorText.includes("already") || 
          errorText.includes("duplicate") ||
          errorText.includes("exists") ||
          err.response?.status === 409) {
        setSuccessPopup({ type: "warning", title: "Already Submitted", message: "This reflection has already been submitted for the selected date and period." });
      } else {
        setSuccessPopup({ type: "error", title: "Save Failed", message: errorMsg || "Something went wrong. Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* AI CHAT */
  const sendChat = async () => {
    if (!chatInput.trim()) return;

    const userText = chatInput;
    setChatMessages(prev => [...prev, { sender: "user", text: userText }]);
    setChatInput("");

    try {
      const fd = new FormData();
      fd.append("question", userText);
      fd.append("top_k", "6");

      const { data } = await askAIChatAPI(fd);

      setChatMessages(prev => [
        ...prev,
        { sender: "ai", text: data.answer || "No matching reflection found." }
      ]);
    } catch {
      setChatMessages(prev => [
        ...prev,
        { sender: "ai", text: "AI server error. Try again." }
      ]);
    }
  };

  /* INITIAL LOAD */
  useEffect(() => {
    if (!isLoggedIn) return;
    loadEmployee();
    loadTeam();

    // ✅ FINAL FIX: Show reminder popup ONLY ONCE per login session.
    // sessionStorage flag prevents re-trigger on chat close / re-renders.
    if (!reminderShownThisSession.current) {
      reminderShownThisSession.current = true;
      sessionStorage.setItem("reminderShown", "true");
      fetchReminder("daily", { showPopup: true });
    }

    loadQuestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return {
    email,
    isLoggedIn,
    isSidebarOpen,
    setIsSidebarOpen,
    isMobile,
    activePage,
    setActivePage,
    profile,
    team,
    userRole,

    period,
    setPeriod,
    selectedDate,
    setSelectedDate,
    questions,
    answers,
    setAnswers,
    submitting,
    setSubmitting,
    polishing,
    isLoading,
    loadQuestions,
    updateAnswer,
    addAnswer,
    removeAnswer,
    polishAnswer,
    saveReflection,

    successPopup,
    setSuccessPopup,

    reminderData,
    hasNewReminder,
    showReminderPopup,
    setShowReminderPopup,
    showReminderPanel,
    setShowReminderPanel,
    fetchReminder,
    setHasNewReminder,

    showAIChat,
    setShowAIChat,
    chatInput,
    setChatInput,
    chatMessages,
    setChatMessages,
    sendChat,
  };
}